import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Plus, RefreshCw, Calendar, Trash2, Play, Pause, 
  Edit, Clock, User, MapPin, FileText, DollarSign 
} from 'lucide-react';
import { jobsAPI, quoteTemplatesAPI } from '../services/api';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const frequencyOptions = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const RecurringTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    address: '',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    budget: '',
    quote_template_id: '',
    assigned_contractors: [],
    auto_create_quote: false,
    auto_send_quote: false,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [templatesRes, quoteTemplatesRes, contractorsRes] = await Promise.all([
        axios.get(`${API}/api/recurring-templates`, { headers }),
        axios.get(`${API}/api/quote-templates`, { headers }),
        axios.get(`${API}/api/contractors`, { headers })
      ]);
      
      setTemplates(templatesRes.data);
      setQuoteTemplates(quoteTemplatesRes.data);
      setContractors(contractorsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.client_name || !formData.start_date) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        end_date: formData.end_date || null,
        quote_template_id: formData.quote_template_id || null
      };
      
      if (editingTemplate) {
        await axios.put(`${API}/api/recurring-templates/${editingTemplate.id}`, payload, { headers });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/api/recurring-templates`, payload, { headers });
        toast.success('Template created');
      }
      
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleToggle = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/recurring-templates/${templateId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle template');
    }
  };

  const handleRunNow = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/recurring-templates/${templateId}/run-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Job created: ${response.data.job?.title}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to create job');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/recurring-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name || '',
      description: template.description || '',
      client_name: template.client_name || '',
      client_email: template.client_email || '',
      client_phone: template.client_phone || '',
      address: template.address || '',
      frequency: template.frequency || 'monthly',
      start_date: template.start_date?.split('T')[0] || '',
      end_date: template.end_date?.split('T')[0] || '',
      budget: template.budget?.toString() || '',
      quote_template_id: template.quote_template_id || '',
      assigned_contractors: template.assigned_contractors || [],
      auto_create_quote: template.auto_create_quote || false,
      auto_send_quote: template.auto_send_quote || false,
      notes: template.notes || ''
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      client_name: '',
      client_email: '',
      client_phone: '',
      address: '',
      frequency: 'monthly',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      budget: '',
      quote_template_id: '',
      assigned_contractors: [],
      auto_create_quote: false,
      auto_send_quote: false,
      notes: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recurring-templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Recurring Job Templates</h1>
          <p className="text-muted-foreground">Automate job creation on a schedule</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" /> New Template
        </Button>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">No Recurring Templates</h3>
            <p className="text-muted-foreground mb-4">Create templates to automatically generate jobs on a schedule</p>
            <Button onClick={() => setShowDialog(true)}>
              <Plus className="h-4 w-4 mr-2" /> Create First Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className={`bg-card border-border ${!template.is_active ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <RefreshCw className={`h-4 w-4 ${template.is_active ? 'text-green-500' : 'text-gray-500'}`} />
                      {template.name}
                    </CardTitle>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      template.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {template.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(template)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(template.id)}>
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm space-y-2">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="h-3.5 w-3.5" />
                    <span>{template.client_name}</span>
                  </div>
                  {template.address && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" />
                      <span className="truncate">{template.address}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    <span className="capitalize">{template.frequency}</span>
                  </div>
                  {template.budget && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <DollarSign className="h-3.5 w-3.5" />
                      <span>${template.budget.toLocaleString()}</span>
                    </div>
                  )}
                  {template.quote_template_name && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <FileText className="h-3.5 w-3.5" />
                      <span>{template.quote_template_name}</span>
                    </div>
                  )}
                </div>

                <div className="pt-2 border-t border-border">
                  <div className="flex justify-between text-xs text-muted-foreground mb-2">
                    <span>Next Run: {formatDate(template.next_run_date)}</span>
                    <span>Created: {template.jobs_created || 0} jobs</span>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => handleToggle(template.id)}
                    >
                      {template.is_active ? <Pause className="h-3.5 w-3.5 mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                      {template.is_active ? 'Pause' : 'Activate'}
                    </Button>
                    <Button 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleRunNow(template.id)}
                      disabled={!template.is_active}
                    >
                      <Play className="h-3.5 w-3.5 mr-1" /> Run Now
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Recurring Template'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Template Name *</label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Monthly Pool Maintenance"
                  className="bg-background"
                />
              </div>
              
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Job description..."
                  className="bg-background"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Client Name *</label>
                <Input 
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  className="bg-background"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Client Email</label>
                <Input 
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                  className="bg-background"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Client Phone</label>
                <Input 
                  value={formData.client_phone}
                  onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                  className="bg-background"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Budget</label>
                <Input 
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({...formData, budget: e.target.value})}
                  placeholder="0.00"
                  className="bg-background"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Address</label>
                <Input 
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="bg-background"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Frequency *</label>
                <Select value={formData.frequency} onValueChange={(v) => setFormData({...formData, frequency: v})}>
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencyOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Start Date *</label>
                <Input 
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="bg-background"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">End Date (Optional)</label>
                <Input 
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  className="bg-background"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Quote Template</label>
                <Select value={formData.quote_template_id || "none"} onValueChange={(v) => setFormData({...formData, quote_template_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="Select template..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {quoteTemplates.map(qt => (
                      <SelectItem key={qt.id} value={qt.id}>{qt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Notes</label>
                <Textarea 
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes..."
                  className="bg-background"
                  rows={2}
                />
              </div>

              <div className="col-span-2 flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                <div>
                  <p className="font-medium">Auto-create Quote</p>
                  <p className="text-xs text-muted-foreground">Automatically create quote from template</p>
                </div>
                <Switch 
                  checked={formData.auto_create_quote}
                  onCheckedChange={(v) => setFormData({...formData, auto_create_quote: v})}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>
              {editingTemplate ? 'Update' : 'Create'} Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringTemplatesPage;
